import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _PropTypes from 'prop-types';
import * as React from 'react';
import styled from 'styled-components';
import { IconCheck } from '../Icons/IconCheck.js';
import { IconDash } from '../Icons/IconDash.js';
import { IconLock } from '../Icons/IconLock.js';
import { CheckboxIcon } from './CheckboxIcon.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["as", "size", "className", "icon", "iconChecked", "presentational", "readOnly", "indeterminate", "type"];
var checkBoxIconSize = {
  16: 12,
  20: 14,
  24: 16,
  32: 24
};
var CheckboxIconChecked = styled(CheckboxIcon).attrs(function (props) {
  return {
    children: /*#__PURE__*/jsx(IconCheck, {
      color: props.theme.neutralColors.white,
      height: checkBoxIconSize[props.size],
      width: checkBoxIconSize[props.size]
    })
  };
}).withConfig({
  displayName: "Checkbox__CheckboxIconChecked",
  componentId: "sc-tti4h3-0"
})(["&&{border-color:", ";background-color:", ";}"], function (props) {
  return props.theme.CTAColors.primary;
}, function (props) {
  return props.theme.CTAColors.primary;
});
var CheckboxIconLocked = styled(CheckboxIcon).attrs(function (props) {
  return {
    children: /*#__PURE__*/jsx(IconLock, {
      color: props.theme.neutralColors.white,
      height: checkBoxIconSize[props.size],
      width: checkBoxIconSize[props.size]
    })
  };
}).withConfig({
  displayName: "Checkbox__CheckboxIconLocked",
  componentId: "sc-tti4h3-1"
})(["&&{border-color:", ";background-color:", ";}"], function (props) {
  return props.theme.CTAColors.primary;
}, function (props) {
  return props.theme.CTAColors.primary;
});
var StyledIconDash = styled(IconDash).withConfig({
  displayName: "Checkbox__StyledIconDash",
  componentId: "sc-tti4h3-2"
})(["&&{width:", "px;}&& path{stroke-width:4px;}"], function (props) {
  return props.size / 1.75;
});
var CheckboxIconIndeterminate = styled(CheckboxIcon).attrs(function (props) {
  return {
    children: /*#__PURE__*/jsx(StyledIconDash, {
      color: props.theme.CTAColors.primary,
      height: checkBoxIconSize[props.size],
      width: checkBoxIconSize[props.size],
      size: props.size
    })
  };
}).withConfig({
  displayName: "Checkbox__CheckboxIconIndeterminate",
  componentId: "sc-tti4h3-3"
})(["&&{border-color:", ";background-color:", ";}"], function (props) {
  return props.theme.CTAColors.primary;
}, function (props) {
  return props.theme.neutralColors.white;
});
var CheckboxInput = styled.input.withConfig({
  displayName: "Checkbox__CheckboxInput",
  componentId: "sc-tti4h3-4"
})(["&&{cursor:inherit;opacity:0;height:100%;width:100%;margin:0;padding:0;position:absolute;top:0;inset-inline-start:0;}"]);
var CheckboxWrapper = styled.div.withConfig({
  displayName: "Checkbox__CheckboxWrapper",
  componentId: "sc-tti4h3-5"
})(["&&{border-radius:", "px;cursor:pointer;font-size:0;line-height:0;position:relative;display:inline-flex;vertical-align:middle;", ";}&&:focus-within{opacity:1 !important;outline:0;}&&:focus-within:before{transform:scale(1) !important;opacity:1 !important;transition:opacity 0.2s,transform,0.2s;}@media (prefers-reduced-motion:reduce){&&:before{transition:none;transform:scale(1);}}"], function (props) {
  return props.$size > 19 ? 4 : 3;
}, function (props) {
  return props.$disabled && "cursor: not-allowed;\n\n        ".concat(CheckboxIcon, " {\n            border-color: ").concat(props.theme.neutralColors.silver, ";\n            background-color: ").concat(props.theme.neutralColors.pearl, ";\n        }\n\n        ").concat(CheckboxIconChecked, ", ").concat(CheckboxIconLocked, ", ").concat(CheckboxIconIndeterminate, " {\n            opacity: 0.5\n        }\n    ");
});
var checkboxComponents = {
  CheckboxWrapper: CheckboxWrapper
};
var Checkbox = /*#__PURE__*/React.forwardRef(function Checkbox2(props, ref) {
  var as = props.as,
      _props$size = props.size,
      size = _props$size === void 0 ? 16 : _props$size,
      className = props.className,
      _props$icon = props.icon,
      Icon = _props$icon === void 0 ? CheckboxIcon : _props$icon,
      _props$iconChecked = props.iconChecked,
      IconChecked = _props$iconChecked === void 0 ? CheckboxIconChecked : _props$iconChecked,
      presentational = props.presentational,
      readOnly = props.readOnly;
      props.indeterminate;
      props.type;
      var rest = _objectWithoutProperties(props, _excluded);

  var checked = props.checked || props.defaultChecked;
  return /*#__PURE__*/jsxs(CheckboxWrapper, {
    $disabled: props.disabled,
    className: className,
    $size: size,
    children: [function () {
      if (props.indeterminate) return /*#__PURE__*/jsx(CheckboxIconIndeterminate, {
        size: size
      });
      if (props.locked) return /*#__PURE__*/jsx(CheckboxIconLocked, {
        size: size
      });
      if (checked) return /*#__PURE__*/jsx(IconChecked, {
        size: size
      });
      return /*#__PURE__*/jsx(Icon, {
        size: size
      });
    }(), /*#__PURE__*/jsx(CheckboxInput, _objectSpread(_objectSpread({}, rest), {}, {
      as: presentational ? "div" : as,
      readOnly: readOnly,
      ref: ref,
      type: (!as || as === "input") && !presentational ? "checkbox" : void 0,
      onChange: readOnly ? void 0 : props.onChange
    }))]
  });
});
Checkbox.propTypes = {
  "as": _PropTypes.oneOfType([_PropTypes.string, _PropTypes.func, _PropTypes.object]),
  "checked": _PropTypes.bool,
  "className": _PropTypes.string,
  "defaultChecked": _PropTypes.bool,
  "disabled": _PropTypes.bool,
  "icon": _PropTypes.object,
  "iconChecked": _PropTypes.object,
  "indeterminate": _PropTypes.bool,
  "locked": _PropTypes.bool,
  "onChange": _PropTypes.func,
  "presentational": _PropTypes.bool,
  "readOnly": _PropTypes.bool,
  "size": _PropTypes.oneOf([16, 20, 24, 32]),
  "type": _PropTypes.string
};
Checkbox.displayName = "Checkbox";

export { Checkbox, checkboxComponents };
